import { AddressElement, useElements } from '@stripe/react-stripe-js';
import { PaymentDetailsDto } from 'api/dtos/subscriptionPlanDtos';
import { useUpdatePaymentMethodBillingMutation } from 'api/subscriptionPlanEndpoints';
import { Heading, Modal, Section } from '@carbon/react';
import { Text } from '@mantine/core';
import { defineMessages, useIntl } from 'react-intl';
import sharedMessages from 'Components/utils/sharedMessages';
import useGetUser from 'hooks/useGetUser';
import styles from './EditPaymentMethodModal.module.scss';

interface Props {
  opened: boolean;
  onClose: () => void;
  paymentMethod: PaymentDetailsDto | null;
}

const EditPaymentMethodModal = ({
    opened,
    onClose,
    paymentMethod,
}: Props) => {
    const elements = useElements();
    const intl = useIntl();

    const [updatePaymentMethod, { isLoading }] = useUpdatePaymentMethodBillingMutation();
    const user = useGetUser();

    if (!user) {
        return null;
    }

    const handleAddressChange = async (event: any) => {
        if (paymentMethod === null) { 
            return;
        }

        if (!elements) return;

        const addressElement = elements.getElement(AddressElement);

        if (!addressElement) {
            return;
        }

        const addressResult = await addressElement.getValue();
        if (!addressResult.complete) {
            return;
        }

        try {
            await updatePaymentMethod({
                paymentMethodId: paymentMethod.paymentToken,
                billingAddress: {
                    fullName: addressResult.value.name,
                    email: user.email,
                    line1: addressResult.value.address.line1,
                    line2: addressResult.value.address.line2,
                    city: addressResult.value.address.city,
                    state: addressResult.value.address.state,
                    postalCode: addressResult.value.address.postal_code,
                    country: addressResult.value.address.country,
                },
            }).unwrap();
            onClose();
        } catch (e) {
            console.log('Nie udało się edytować metody płatności'); // TO_DO
        }
    };

    return (
        <Modal
            preventCloseOnClickOutside
            open={opened}
            onRequestClose={onClose}
            loadingStatus={isLoading ? 'active' : undefined}
            primaryButtonDisabled={isLoading}
            title={intl.formatMessage(messages.editPaymentMethod)}
            onRequestSubmit={handleAddressChange}
            modalHeading={intl.formatMessage(messages.areYouSure)}
            modalLabel={intl.formatMessage(messages.changingCard)}
            primaryButtonText={intl.formatMessage(sharedMessages.submit)}
            closeButtonLabel={intl.formatMessage(sharedMessages.cancel)}
            secondaryButtonText={intl.formatMessage(sharedMessages.back)}
        >
            {paymentMethod && (
                <Section>
                    <Section>
                        <Text>
                            {paymentMethod.cardBrand.toUpperCase()}
                            {' '}
                            **** **** ****
                            {' '}
                            {paymentMethod.lastFourDigits}
                        </Text>
                        <Text>
                            {intl.formatMessage(sharedMessages.validUntil)}
                            : 
                            {' '}
                            {paymentMethod.expirationMonth}
                            /
                            {paymentMethod.expirationYear}
                        </Text>
                    </Section>

                    <Section>
                        <Heading className={styles.heading}>
                            {intl.formatMessage(sharedMessages.billingAddress)}
                        </Heading>

                        <AddressElement
                            options={{
                                mode: 'billing',
                                defaultValues: {
                                    name: paymentMethod.billingAddress.fullName,
                                    address: {
                                        line1: paymentMethod.billingAddress.line1,
                                        line2: paymentMethod.billingAddress.line2,
                                        city: paymentMethod.billingAddress.city,
                                        state: paymentMethod.billingAddress.state,
                                        postal_code: paymentMethod.billingAddress.postalCode,
                                        country: paymentMethod.billingAddress.country,
                                    },
                                },
                            }}
                        />
                    </Section>

                    <p className={styles.explanation}>
                        {intl.formatMessage(messages.explanation)}
                    </p>
                </Section>
            )}
        </Modal>
    );
};

export default EditPaymentMethodModal;

const messages = defineMessages({
    editPaymentMethod: {
        id: 'src.Screens.Dashboard.Subscription.PaymentMethods.EditPaymentMethodModal.EditPaymentMethodModal.editPaymentMethod',
        defaultMessage: 'Edytuj metodę płatności',
    },
    changingCard: {
        id: 'src.Screens.Dashboard.Subscription.PaymentMethods.EditPaymentMethodModal.EditPaymentMethodModal.changingCard',
        defaultMessage: 'Zmiana danych przypisanych do karty',
    },
    areYouSure: {
        id: 'src.Screens.Dashboard.Subscription.PaymentMethods.EditPaymentMethodModal.EditPaymentMethodModal.areYouSure',
        defaultMessage: 'Jesteś pewien, że chcesz zmienić dane przypisane karty?',
    },
    explanation: {
        id: 'src.Screens.Dashboard.Subscription.PaymentMethods.EditPaymentMethodModal.EditPaymentMethodModal.explanation',
        defaultMessage: 'Ze względów bezpieczeństwa dane karty nie mogą być edytowane. Aby zmienić kartę, dodaj nową metodę płatności.',
    },
});
